import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, FileText, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";

const fadeUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 },
};

const terms = [
  {
    title: "No Guaranteed Outcomes",
    content: "Building startups is inherently uncertain. We provide expertise, execution, and support, but we cannot guarantee specific results. Success depends on many factors, including market conditions, timing, and your own execution. Anyone who promises guaranteed outcomes is lying.",
  },
  {
    title: "Clarity, Not Promises",
    content: "Our role is to help you find clarity, to validate or invalidate assumptions, to understand what's working and what isn't, to make better decisions faster. That clarity might lead to a successful launch. It might lead to a pivot. It might lead to a decision not to proceed. All of these are valuable outcomes.",
  },
  {
    title: "Mutual Respect",
    content: "We expect professional, respectful communication. We'll be honest with you, sometimes uncomfortably so. We expect the same in return. If the working relationship becomes unproductive, either party can end the engagement.",
  },
  {
    title: "Limited Engagements",
    content: "We take on a limited number of founders at any given time. Quality over quantity. This means we may not be able to start immediately, and we may not be able to take on every founder who wants to work with us.",
  },
  {
    title: "Transparency",
    content: "We'll be clear about what we're doing, why we're doing it, and what we're learning. You'll have visibility into our process, our thinking, and our recommendations. No black boxes.",
  },
  {
    title: "Confidentiality",
    content: "Everything you share with us stays confidential. We won't share your idea, your data, or our conversations with anyone without your explicit permission.",
  },
  {
    title: "Intellectual Property",
    content: "Work we create for you belongs to you. We retain the right to use general learnings and methodologies in future work, but your specific product, code, and business information are yours.",
  },
  {
    title: "Payment Terms",
    content: "Payment terms will be outlined in your specific agreement. Typically, we require a deposit to begin work, with remaining payments tied to milestones or monthly invoicing. Late payments may result in paused work.",
  },
  {
    title: "Termination",
    content: "Either party can end the engagement with reasonable notice (typically 2 weeks). You'll be responsible for work completed up to that point. We'll do our best to wrap up cleanly and hand off any work in progress.",
  },
];

export default function TermsPage() {
  return (
    <div className="flex flex-col">
      {/* Hero */}
      <section className="bg-hero section-padding">
        <div className="container-narrow">
          <motion.div
            initial="initial"
            animate="animate"
            variants={fadeUp}
            className="text-center"
          >
            <FileText className="h-12 w-12 text-accent mx-auto mb-6" />
            <h1 className="heading-display text-foreground mb-6">
              Terms & Conditions
            </h1>
            <p className="body-large text-muted-foreground max-w-2xl mx-auto">
              Simple, clear terms. No legal jargon. Just honest expectations.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Notice */}
      <section className="bg-background py-8">
        <div className="container-narrow">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="bg-accent/5 border border-accent/20 rounded-xl p-6 flex items-start gap-4"
          >
            <AlertCircle className="h-5 w-5 text-accent flex-shrink-0 mt-0.5" />
            <div>
              <p className="text-foreground font-medium mb-1">Before We Begin</p>
              <p className="text-sm text-muted-foreground">
                These terms are meant to set expectations clearly. When we start working together, you'll receive a specific agreement tailored to your engagement. These general terms inform that agreement.
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Terms */}
      <section className="bg-background section-padding">
        <div className="container-narrow">
          <div className="space-y-8">
            {terms.map((term, i) => (
              <motion.div
                key={term.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
              >
                <div className="flex items-start gap-4">
                  <span className="text-accent font-serif text-xl font-medium w-8 flex-shrink-0">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <div>
                    <h3 className="font-serif text-xl font-medium text-foreground mb-3">{term.title}</h3>
                    <p className="text-muted-foreground leading-relaxed">{term.content}</p>
                  </div>
                </div>
                {i < terms.length - 1 && <Separator className="mt-8" />}
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Last Updated */}
      <section className="bg-section py-8">
        <div className="container-narrow text-center">
          <p className="text-sm text-muted-foreground">
            Last updated: {new Date().toLocaleDateString('en-US', { month: 'long', year: 'numeric' })}
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-primary text-primary-foreground section-padding-sm">
        <div className="container-narrow text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
          >
            <h2 className="heading-section mb-4">Questions About Terms?</h2>
            <p className="body-base text-primary-foreground/80 mb-6">
              Reach out. We're happy to clarify anything.
            </p>
            <Button variant="secondary" size="lg" asChild className="bg-primary-foreground text-primary hover:bg-primary-foreground/90">
              <Link to="/contact">
                Contact Us
                <ArrowRight className="h-4 w-4 ml-1" />
              </Link>
            </Button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
